import {
  MediaCardMetadata,
  MediaCardType,
  MediaString,
} from '@wandb/weave/common/types/media';
import {useMemo} from 'react';

import {useRunSetsQuery} from '../../state/runs/hooks';
import {Grouping} from '../../state/views/groupSelections/types';
import {runColor} from '../../util/colors';
import {ClassLabelMap, classLabelMap, getMediaMetadata} from '../../util/media';
import {
  WANDB_BBOX_CLASS_LABEL_KEY,
  WANDB_MASK_CLASS_LABEL_KEY,
} from '../../util/mediaConstants';
import {RunWithHistoryAndMediaWandb} from '../MediaCard';
import {usePanelsSharedRefsContext} from '../Views/contexts/PanelsSharedRefsContext';
import {useWorkspaceSettingsColorPalette} from '../WorkspaceDrawer/Settings/colorPalette/ColorPalette';
import {DEFAULT_LINE_PLOT_NUM_RUNS_NEW} from '../WorkspaceDrawer/Settings/runLinePlots/maxRunDefaults';
import {
  Axis,
  KeyTypes,
  MediaBrowserPanelConfig,
  rowsWithKeysMemo,
} from './common';
import {
  Layout,
  MediaBrowserPanelProps,
  MediaConfigWithDefaults,
  MediaConfigWithDefaultsAndMediaKeys,
} from './Component';
import * as PANEL_MEDIA_BROWSER_CONFIG from './config';
import {useMediaKeys} from './hooks/useMediaKeys';
import {useMediaSliderKey} from './hooks/useMediaSliderKey';
import {useStepMeta} from './hooks/useStepMeta';
import {makeLayout} from './makeLayout';
import {TilePagination} from './tiles';
import {StepMeta} from './types';
import {useMediaCardTypes} from './useGetMediaCardTypes';
import {getCurrentStepAndSliderValue} from './utils/stepUtils';

type MediaPanelValues = {
  mediaType: MediaCardType | null;
  hasBoxes: boolean;
  hasMasks: boolean;
  layout: Layout;
  tilePagination: TilePagination | null;
  mediaKeys: string[] | undefined;
  numericKeys: string[] | undefined;
  defaultKeys: string[] | undefined;
  allMediaKeys: string[];
  keyTypes: KeyTypes;
  mediaString: MediaString | null;
  flattenedMediaMetadataForAllRuns: MediaCardMetadata[];
  firstMediaMetadata: MediaCardMetadata | null;
  currentStep: number;
  currentSliderValue: number;
  stepMeta: StepMeta;
  allMasks: ClassLabelMap;
  allBoxes: ClassLabelMap;
  configWithDefaults: MediaConfigWithDefaults;
  configWithDefaultsAndMediaKeys: MediaConfigWithDefaultsAndMediaKeys | null;
  sizingSettings: {
    columnCount: {
      min: number;
      max: number;
      current: number;
    };
  };
  snapToExistingStep: boolean;
  runsWithMedia: RunWithHistoryAndMediaWandb[];
};

function defaultColumnCount(
  mediaType: MediaCardType | null,
  mediaCount?: number
): number {
  if (mediaCount != null && mediaCount < 4) {
    return mediaCount;
  }
  if (mediaType === 'object3D' || mediaType === 'video') {
    return 4;
  }
  if (mediaType === 'image') {
    return Math.min(3, mediaCount ?? Infinity);
  }
  return 1;
}

export function getConfigWithDefaults(
  config: MediaBrowserPanelConfig,
  mediaType: MediaCardType | null,
  mediaSliderKey: string | undefined,
  isSingleRun: boolean,
  mediaCount?: number
): MediaConfigWithDefaults {
  // Set sane defaults
  const gallerySettings =
    config.gallerySettings ??
    (isSingleRun
      ? {
          axis: 'index' as Axis,
        }
      : {
          axis: 'run' as Axis,
        });

  const gridSettings =
    config.gridSettings ??
    (isSingleRun
      ? {
          xAxis: 'step' as Axis,
          yAxis: 'index' as Axis,
        }
      : {xAxis: 'step' as Axis, yAxis: 'run' as Axis});

  const mode = config.mode ?? 'gallery';
  const pixelated = config.pixelated ?? true;

  // Actual Size and Fit to 1 Dimension is currently restricted to gallery mode
  let actualSize = false;
  let fitToDimension = false;
  if (mode === 'gallery') {
    actualSize = config.actualSize ?? false;
    fitToDimension = config.fitToDimension ?? false;
  }

  const columnCount =
    config.columnCount ?? defaultColumnCount(mediaType, mediaCount);

  const maxRuns = config.maxRuns ?? DEFAULT_LINE_PLOT_NUM_RUNS_NEW;

  const maxYAxisCount =
    config.maxYAxisCount ?? PANEL_MEDIA_BROWSER_CONFIG.DEFAULT_MAX_Y_AXIS_COUNT;

  const snapToExistingStep = config.snapToExistingStep ?? true;
  return {
    ...config,
    actualSize,
    fitToDimension,
    columnCount,
    mode,
    pixelated,
    gallerySettings,
    gridSettings,
    maxRuns,
    maxYAxisCount,
    snapToExistingStep,
    mediaSliderKey,
  };
}

export const useOverlays = ({
  allRuns,
  mediaKeys,
}: {
  allRuns: RunWithHistoryAndMediaWandb[];
  mediaKeys: string[] | undefined;
}) => {
  const {allMasks, hasMasks, allBoxes, hasBoxes} = useMemo(() => {
    const masks = classLabelMap(
      allRuns,
      mediaKeys ?? [],
      WANDB_MASK_CLASS_LABEL_KEY
    );
    const boxes = classLabelMap(
      allRuns,
      mediaKeys ?? [],
      WANDB_BBOX_CLASS_LABEL_KEY
    );
    return {
      allMasks: masks,
      hasMasks: Object.keys(masks).length > 0,
      allBoxes: boxes,
      hasBoxes: Object.keys(boxes).length > 0,
    };
  }, [allRuns, mediaKeys]);

  return {
    allMasks,
    hasMasks,
    allBoxes,
    hasBoxes,
  };
};

export const useAllRuns = ({
  data,
  query,
  pageQuery,
  customRunColors,
}: Pick<
  MediaBrowserPanelProps,
  'data' | 'pageQuery' | 'customRunColors' | 'query'
>) => {
  const {runSetRefs} = usePanelsSharedRefsContext();

  const runSetsInfo = useRunSetsQuery({runSetRefs});
  const idToGroupingMap = useMemo(() => {
    const map: {[key: string]: Grouping | undefined} = {};
    (pageQuery.runSets || []).forEach(runSet => {
      map[runSet.id] = runSet.grouping;
    });
    return map;
  }, [pageQuery.runSets]);

  const runSetEnabledMap = useMemo(() => {
    const map = new Map();
    runSetsInfo.runSets?.forEach(s => {
      if (s.enabled === true) {
        map.set(s.id, true);
      }
    });
    return map;
  }, [runSetsInfo.runSets]);

  const queryMap = useMemo(() => {
    const map = new Map();
    query.queries.forEach(q => {
      map.set(q.id, q);
    });
    return map;
  }, [query.queries]);

  const filteredRuns = useMemo(() => {
    return data.histories.data.filter(r => {
      const run = data?.filteredRunsById?.[r.name];
      const runsetID = run?.runsetInfo?.id;
      return runsetID != null && runSetEnabledMap.has(runsetID);
    });
  }, [data.histories.data, data.filteredRunsById, runSetEnabledMap]);

  const colorPaletteOption = useWorkspaceSettingsColorPalette();

  const allRuns: RunWithHistoryAndMediaWandb[] = useMemo(() => {
    return filteredRuns.map(r => {
      const run = data.filteredRunsById[r.name];
      const runsetID = run.runsetInfo.id;
      const runsetQuery = queryMap.get(runsetID);

      const groupKeys = idToGroupingMap[run.runsetInfo.id];
      const color = groupKeys
        ? runColor({run, groupKeys, customRunColors, colorPaletteOption})
        : undefined;

      if (runsetQuery == null) {
        throw new Error(
          'Inconsistent data from the server, run does not have a matching runset'
        );
      }
      return {
        ...r,
        _wandb: run._wandb,
        entityName: runsetQuery.entityName,
        projectName: runsetQuery.projectName,
        color,
      };
    });
  }, [
    filteredRuns,
    data.filteredRunsById,
    queryMap,
    idToGroupingMap,
    customRunColors,
    colorPaletteOption,
  ]);
  return allRuns;
};

export function useConfigWithDefaults({
  data,
  query,
  config,
  customRunColors,
  pageQuery,
}: Pick<
  MediaBrowserPanelProps,
  'data' | 'query' | 'config' | 'customRunColors' | 'pageQuery'
>): {
  configWithDefaults: MediaConfigWithDefaults;
  allMasks: ClassLabelMap;
  hasMasks: boolean;
  allBoxes: ClassLabelMap;
  hasBoxes: boolean;
} {
  const {
    mediaKeys,
    mediaSliderKey: mediaSliderKeyFromConfig,
    stepIndex: stepFromConfig,
    snapToExistingStep = true,
  } = config;

  // TODO: replace this with `useSingleRunModeContext().isSingleRun`
  // (requires updating various snapshot tests)
  const isSingleRun = useMemo(() => {
    return data.histories.data.length === 1;
  }, [data.histories.data.length]);

  const firstMediaKey = useMemo(() => {
    return mediaKeys?.[0] ?? null;
  }, [mediaKeys]);

  // Individual panels do not support multiple media types.
  const mediaType = useMediaCardTypes(mediaKeys)?.[0] ?? null;

  const mediaSliderKey = useMediaSliderKey(mediaSliderKeyFromConfig);

  const allRuns: RunWithHistoryAndMediaWandb[] = useAllRuns({
    data,
    query,
    pageQuery,
    customRunColors,
  });

  // For now we memoize the function globally
  const runsWithMedia = useMemo(() => {
    const filtered = allRuns.filter(runHistory => {
      const steps = rowsWithKeysMemo(runHistory, mediaKeys);
      return steps.length > 0;
    });

    return filtered;
  }, [allRuns, mediaKeys]);

  // Cache rowsWithKeysMemo results for each run
  const runStepsMap = useMemo(() => {
    const map = new Map();
    runsWithMedia.forEach(runHistory => {
      map.set(runHistory.name, rowsWithKeysMemo(runHistory, mediaKeys));
    });
    return map;
  }, [runsWithMedia, mediaKeys]);

  const stepMeta = useStepMeta(
    runsWithMedia,
    mediaSliderKey,
    snapToExistingStep,
    runStepsMap,
    mediaKeys
  );

  const {currentStep} = useMemo(() => {
    return getCurrentStepAndSliderValue(
      stepMeta,
      stepFromConfig,
      snapToExistingStep,
      mediaSliderKey
    );
  }, [stepMeta, stepFromConfig, snapToExistingStep, mediaSliderKey]);

  const firstHistory = useMemo(
    () => data.histories.data[0]?.history ?? null,
    [data.histories.data]
  );
  const firstMediaMetadata =
    firstMediaKey != null && firstHistory != null
      ? getMediaMetadata(firstHistory, firstMediaKey, currentStep)
      : null;
  const mediaCount = useMemo(
    () =>
      isSingleRun
        ? ((firstMediaMetadata && firstMediaMetadata.count) ?? 1)
        : data.histories.data.length,
    [isSingleRun, firstMediaMetadata, data.histories.data.length]
  );
  const {allMasks, hasMasks, allBoxes, hasBoxes} = useOverlays({
    allRuns,
    mediaKeys,
  });
  const firstMaskCount =
    firstMediaKey != null && allMasks[firstMediaKey] != null
      ? Object.keys(allMasks[firstMediaKey]).length
      : 0;

  const configWithDefaults = useMemo(
    () =>
      getConfigWithDefaults(
        config,
        mediaType,
        mediaSliderKey,
        isSingleRun,
        mediaCount * (firstMaskCount + 1)
      ),
    [config, mediaType, isSingleRun, mediaCount, firstMaskCount, mediaSliderKey]
  );

  return {
    configWithDefaults,
    allMasks,
    hasMasks,
    allBoxes,
    hasBoxes,
  };
}

export function useMediaPanelValues(
  props: MediaBrowserPanelProps
): MediaPanelValues {
  const {data, query, config, customRunColors} = props;
  const {runSetRefs} = usePanelsSharedRefsContext();
  const runSetsInfo = useRunSetsQuery({runSetRefs});

  const idToGroupingMap = useMemo(() => {
    const map: {[key: string]: Grouping | undefined} = {};
    (props.pageQuery.runSets || []).forEach(runSet => {
      map[runSet.id] = runSet.grouping;
    });
    return map;
  }, [props.pageQuery.runSets]);

  const {
    mediaKeys,
    mediaSliderKey: mediaSliderKeyFromConfig,
    stepIndex: stepFromConfig,
    snapToExistingStep = true,
  } = config;

  const {allMediaKeys, defaultKeys, keyTypes, numericKeys} = useMediaKeys();

  const firstMediaKey = useMemo(() => {
    return mediaKeys?.[0] ?? null;
  }, [mediaKeys]);

  const mediaString = useMemo(() => {
    return firstMediaKey != null
      ? (keyTypes[firstMediaKey] as MediaString)
      : null;
  }, [firstMediaKey, keyTypes]);
  // TODO: relax this to allow for multiple media types
  const mediaType = useMediaCardTypes(mediaKeys)?.[0];

  const mediaSliderKey = useMediaSliderKey(mediaSliderKeyFromConfig);

  const runSetEnabledMap = useMemo(() => {
    const map = new Map();
    runSetsInfo.runSets?.forEach(s => {
      if (s.enabled === true) {
        map.set(s.id, true);
      }
    });
    return map;
  }, [runSetsInfo.runSets]);

  const queryMap = useMemo(() => {
    const map = new Map();
    query.queries.forEach(q => {
      map.set(q.id, q);
    });
    return map;
  }, [query.queries]);

  const filteredRuns = useMemo(() => {
    return data.histories.data.filter(r => {
      const run = data?.filteredRunsById?.[r.name];
      const runsetID = run?.runsetInfo?.id;
      return runsetID != null && runSetEnabledMap.has(runsetID);
    });
  }, [data.histories.data, data.filteredRunsById, runSetEnabledMap]);

  const colorPaletteOption = useWorkspaceSettingsColorPalette();

  const allRuns: RunWithHistoryAndMediaWandb[] = useMemo(() => {
    return filteredRuns.map(r => {
      const run = data.filteredRunsById[r.name];
      const runsetID = run.runsetInfo.id;
      const runsetQuery = queryMap.get(runsetID);

      const groupKeys = idToGroupingMap[run.runsetInfo.id];
      const color = groupKeys
        ? runColor({run, groupKeys, customRunColors, colorPaletteOption})
        : undefined;

      if (runsetQuery == null) {
        throw new Error(
          'Inconsistent data from the server, run does not have a matching runset'
        );
      }
      return {
        ...r,
        _wandb: run._wandb,
        entityName: runsetQuery.entityName,
        projectName: runsetQuery.projectName,
        color,
      };
    });
  }, [
    filteredRuns,
    data.filteredRunsById,
    queryMap,
    idToGroupingMap,
    customRunColors,
    colorPaletteOption,
  ]);

  // For now we memoize the function globally
  const runsWithMedia = useMemo(() => {
    const filtered = allRuns.filter(runHistory => {
      const steps = rowsWithKeysMemo(runHistory, mediaKeys);
      return steps.length > 0;
    });

    return filtered;
  }, [allRuns, mediaKeys]);

  // Cache rowsWithKeysMemo results for each run
  const runStepsMap = useMemo(() => {
    const map = new Map();
    runsWithMedia.forEach(runHistory => {
      map.set(runHistory.name, rowsWithKeysMemo(runHistory, mediaKeys));
    });
    return map;
  }, [runsWithMedia, mediaKeys]);

  const stepMeta = useStepMeta(
    runsWithMedia,
    mediaSliderKey,
    snapToExistingStep,
    runStepsMap,
    mediaKeys
  );

  const {currentSliderValue, currentStep} = useMemo(() => {
    return getCurrentStepAndSliderValue(
      stepMeta,
      stepFromConfig,
      snapToExistingStep,
      mediaSliderKey
    );
  }, [stepMeta, stepFromConfig, snapToExistingStep, mediaSliderKey]);

  const firstHistory = useMemo(
    () => data.histories.data[0]?.history ?? null,
    [data.histories.data]
  );
  const firstMediaMetadata =
    firstMediaKey != null && firstHistory != null
      ? getMediaMetadata(firstHistory, firstMediaKey, currentStep)
      : null;

  const {configWithDefaults, allMasks, hasMasks, allBoxes, hasBoxes} =
    useConfigWithDefaults({
      data,
      query,
      config,
      customRunColors,
      pageQuery: props.pageQuery,
    });
  const configWithDefaultsAndMediaKeys = useMemo(() => {
    if (mediaKeys == null) {
      return null;
    }
    return {
      ...configWithDefaults,
      mediaKeys,
    };
  }, [configWithDefaults, mediaKeys]);

  const mappedRuns = useMemo(() => {
    return runsWithMedia.map(run => ({
      name: run.name,
      displayName: run.displayName,
      entityName: run.entityName,
      projectName: run.projectName,
      history: run.history,
      notes: run.notes,
      color: run.color,
      _wandb: run._wandb,
    }));
  }, [runsWithMedia]);

  const layout = useMemo(() => {
    // Early return if we don't have the required data
    if (runsWithMedia.length === 0 || stepMeta.runs.length === 0) {
      return {tiles: []};
    }

    return makeLayout({
      runs: mappedRuns,
      stepMeta,
      config: configWithDefaults,
      allMasks,
      currentStep: currentSliderValue,
    });
  }, [
    mappedRuns,
    stepMeta,
    configWithDefaults,
    allMasks,
    currentSliderValue,
    runsWithMedia.length,
  ]);

  const flattenedMediaMetadataForAllRuns = useMemo(() => {
    const result: MediaCardMetadata[] = [];
    const seenKeys = new Set<string>();

    for (const r of runsWithMedia) {
      if (!r.history) {
        continue;
      }

      for (const h of r.history) {
        if (!mediaKeys) {
          continue;
        }

        for (const k of mediaKeys) {
          const metadata = h[k];
          if (metadata == null) {
            continue;
          }

          // Deduplicate based on a unique key
          const uniqueKey = `${r.name}-${h._step}-${k}`;
          if (seenKeys.has(uniqueKey)) {
            continue;
          }

          seenKeys.add(uniqueKey);
          result.push(metadata);
        }
      }
    }
    return result;
  }, [runsWithMedia, mediaKeys]);

  const sizingSettings = useMemo(() => {
    return {
      columnCount: {
        min: PANEL_MEDIA_BROWSER_CONFIG.MIN_COL_COUNT,
        max: PANEL_MEDIA_BROWSER_CONFIG.MAX_COL_COUNT,
        current: configWithDefaults.columnCount,
      },
    };
  }, [configWithDefaults.columnCount]);

  const columnCount = configWithDefaults.columnCount;
  const tilePagination: TilePagination | null = useMemo(() => {
    if (configWithDefaultsAndMediaKeys?.mode !== 'gallery') {
      return null;
    }

    let maxTiles = null;
    if (mediaType === 'plotly') {
      // Plotly cards perform poorly past 8 on a page. This value allows us to
      // perform well with the default 2 panels in a section open.
      maxTiles = 4;
    } else if (mediaType === 'image') {
      // In addition, images may be quite large, so we should paginate them at
      // some reasonable boundary, while also trying to maintain an attractive
      // grid-looking UI where possible.
      //
      // The notion is that each page of data should look "full" (i.e. it
      // appears as a grid where every cell is populated) as much as possible,
      // except for the last page which can be partially-empty. This should be
      // true regardless of the number of configured columns in the UI. Some
      // examples:
      //
      // If the user picks 8 columns, they get a maximum of four rows (32 max
      // images / 8 columns = 4 rows). If they pick 11 columns, they get a
      // maximum of two rows (32 max images / 11 columns = ~2.9 rows, round down
      // to 2), but where every grid cell will be populated unless it's the last
      // page. This means there are less-great column count choices (>=17 yields
      // only a single row), but there's a consistent expectation in the UI that
      // if the grid is fully-populated there's probably another page of data to
      // look at.
      //
      maxTiles =
        Math.floor(
          PANEL_MEDIA_BROWSER_CONFIG.MAXIMUM_MEDIA_PANEL_IMAGE_COUNT /
            columnCount
        ) * columnCount;
    }

    if (maxTiles == null || maxTiles >= layout.tiles.length) {
      return null;
    }

    return {
      start: configWithDefaultsAndMediaKeys?.page?.start ?? 0,
      size: maxTiles,
      total: layout.tiles.length,
    };
  }, [
    configWithDefaultsAndMediaKeys?.mode,
    mediaType,
    columnCount,
    layout.tiles.length,
    configWithDefaultsAndMediaKeys?.page?.start,
  ]);

  return {
    mediaType: mediaType ?? null,
    hasBoxes,
    hasMasks,
    layout,
    tilePagination,
    mediaKeys,
    numericKeys,
    defaultKeys,
    allMediaKeys,
    keyTypes,
    mediaString,
    flattenedMediaMetadataForAllRuns,
    firstMediaMetadata,
    currentStep,
    currentSliderValue,
    stepMeta,
    allMasks,
    allBoxes,
    configWithDefaults,
    configWithDefaultsAndMediaKeys,
    sizingSettings,
    snapToExistingStep,
    runsWithMedia,
  };
}
